import { COMPANY_INFO } from "../../../mocks/aegis-data";

export interface WhyBenefit {
  icon: string;
  title: string;
  description: string;
}

interface WhyChooseUsProps {
  title: string;
  subtitle: string;
  benefits: WhyBenefit[];
  stats?: { value: string; label: string }[];
}

export default function WhyChooseUs({ title, subtitle, benefits, stats }: WhyChooseUsProps) {
  return (
    <section className="bg-white py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        {/* Top label */}
        <div className="flex items-center gap-4 mb-16">
          <div className="h-px w-10 bg-aegisGold" />
          <span className="font-inter text-xs tracking-[0.2em] uppercase text-aegisGold">Why Aegis</span>
          <div className="flex-1 h-px bg-aegisStone" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Left: Full-bleed image with overlays */}
          <div className="relative overflow-hidden" style={{ minHeight: "600px" }}>
            <img
              src="https://public.readdy.ai/ai/img_res/50da7b116361fa87635be89997beb0cc.jpg"
              alt="Aegis Construction Services in-house crews Sarasota Florida"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            {/* Overlays */}
            <div className="absolute inset-0 bg-gradient-to-t from-aegisDark via-aegisDark/30 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-aegisDark/20" />
            {/* Left edge gold line */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-aegisGold" />

            {/* Bottom content */}
            <div className="absolute bottom-0 left-0 right-0 p-10">
              <span className="font-inter text-xs tracking-[0.18em] uppercase text-aegisGold block mb-2">
                Founded &amp; Led By
              </span>
              <p className="font-cormorant font-semibold text-white text-3xl leading-tight mb-1">
                Armando Oyola-Delgado
              </p>
              <p className="font-inter text-white/50 text-sm mb-8">
                Construction Professional Since 1998
              </p>

              {/* Credential stats row */}
              <div className="grid grid-cols-2 gap-0 border border-white/15">
                <div className="px-6 py-5 border-r border-white/15">
                  <p className="font-cormorant font-bold text-aegisGold text-3xl leading-none">1,000+</p>
                  <p className="font-inter text-xs text-white/40 tracking-widest uppercase mt-1">Homes Built</p>
                </div>
                <div className="px-6 py-5">
                  <p className="font-cormorant font-bold text-white text-3xl leading-none">25+</p>
                  <p className="font-inter text-xs text-white/40 tracking-widest uppercase mt-1">Years Experience</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Benefits */}
          <div className="bg-aegisCream flex flex-col justify-center p-12 lg:p-14">
            <h2 className="font-cormorant font-semibold text-aegisDark leading-[1.1] mb-5" style={{ fontSize: "clamp(2rem, 3.2vw, 3rem)" }}>
              {title}
            </h2>
            <div className="w-12 h-0.5 bg-aegisGold mb-7" />
            <p className="font-inter text-aegisMuted text-base leading-relaxed mb-10">
              {subtitle}
            </p>

            {/* Benefits */}
            <div className="space-y-0 divide-y divide-aegisStone">
              {benefits.map((b, i) => (
                <div key={i} className="group flex items-start gap-5 py-5 cursor-default">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 border border-aegisStone bg-white group-hover:border-aegisGold transition-colors duration-300 mt-0.5">
                    <i className={`${b.icon} text-aegisGold text-lg`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-inter font-semibold text-aegisDark text-sm mb-1.5 group-hover:text-aegisGold transition-colors duration-200">
                      {b.title}
                    </h4>
                    <p className="font-inter text-aegisMuted text-sm leading-relaxed">
                      {b.description}
                    </p>
                  </div>
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <i className="ri-arrow-right-s-line text-aegisGold" />
                  </div>
                </div>
              ))}
            </div>

            {/* Stats row */}
            {stats && stats.length > 0 && (
              <div className="grid grid-cols-3 gap-5 mt-8 pt-8 border-t border-aegisStone">
                {stats.map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="font-cormorant font-bold text-aegisGold text-3xl leading-none">{s.value}</p>
                    <p className="font-inter text-aegisMuted text-xs tracking-widest uppercase mt-1.5">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* CTA */}
            <div className="mt-10 pt-8 border-t border-aegisStone">
              <a
                href={COMPANY_INFO.phoneHref}
                className="inline-flex items-center gap-3 bg-aegisDark text-white font-inter font-semibold text-sm tracking-widest uppercase px-8 py-4 whitespace-nowrap cursor-pointer hover:bg-aegisGold transition-colors duration-300"
              >
                <i className="ri-phone-line" />
                Discuss Your Project
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
